import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Done, GetApp, Replay, Warning } from "@material-ui/icons"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useState } from "react"
import type { CartelaItem } from "../../../services/cartelasFornecedor"
import {
    expirarCartelas,
    obterCartelas,
    reativarCartela,
    resgatarCartela,
} from "../../../services/cartelasFornecedor"
import useLoadingStore from "../../../stores/LoadingStore"
import { useCartelasFornecedorContext } from "../context/useCartelasFornecedorContext"

const useStyles = makeStyles(theme => ({
    card: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(2),
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    semResultado: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#7f8c8d",
    },
    btnExportar: {
        borderRadius: 20,
        fontSize: "0.8rem",
    },
    adminCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginTop: theme.spacing(3),
    },
    adminHeader: {
        backgroundColor: "#fff3f3",
        padding: theme.spacing(2),
        borderBottom: "2px solid #ffcccc",
    },
    adminContent: {
        padding: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    btnExpirarAndamento: {
        borderRadius: 25,
        backgroundColor: "#ff9800",
        color: "white",
        "&:hover": { backgroundColor: "#f57c00" },
    },
    btnExpirarCompletas: {
        borderRadius: 25,
        backgroundColor: "var(--cor-erro)",
        color: "white",
        "&:hover": { backgroundColor: "var(--cor-erro-hover)" },
    },
}))

function formatarData(ts: number | null): string {
    if (!ts) return "-"
    return new Date(ts).toLocaleDateString("pt-BR")
}

function chipStatus(status: string) {
    const cores: Record<string, string> = {
        "Em andamento": "#1976d2",
        Completa: "#4caf50",
        Expirada: "#9e9e9e",
        Resgatada: "#9c27b0",
    }
    return (
        <span
            style={{
                background: cores[status] || "#ccc",
                color: "white",
                borderRadius: 12,
                padding: "2px 8px",
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
            }}
        >
            {status}
        </span>
    )
}

function gerarCsv(cartelas: CartelaItem[]): void {
    const linhas = [
        [
            "Nome",
            "Telefone",
            "Data Criação",
            "Data Validade",
            "Última Atualização",
            "Status",
            "Qtd. Selos",
        ],
        ...cartelas.map(c => [
            c.nome ?? "",
            c.telefone ?? "",
            formatarData(c.dataCriacao),
            formatarData(c.dataValidade),
            formatarData(c.dataUltimaAtualizacao),
            c.status ?? "",
            String(c.quantidadeSelos ?? ""),
        ]),
    ]
    const csv = linhas.map(l => l.map(v => `"${v.replace(/"/g, '""')}"`).join(";")).join("\n")
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "cartelas.csv"
    link.click()
    URL.revokeObjectURL(url)
}

interface DialogConfirm {
    aberto: boolean
    titulo: string
    mensagem: string
    onConfirmar: () => void
}

const dialogFechado: DialogConfirm = {
    aberto: false,
    titulo: "",
    mensagem: "",
    onConfirmar: () => undefined,
}

export function TabelaCartelas() {
    const classes = useStyles()
    const { dados, dispatch, buscar } = useCartelasFornecedorContext()
    const { enqueueSnackbar } = useSnackbar()
    const setLoading = useLoadingStore(s => s.setLoading)
    const [dialogo, setDialogo] = useState<DialogConfirm>(dialogFechado)

    const confirmar = (titulo: string, mensagem: string, onConfirmar: () => void) => {
        setDialogo({ aberto: true, titulo, mensagem, onConfirmar })
    }

    const fecharDialogo = () => setDialogo(dialogFechado)

    const handleMudarPagina = (_: unknown, novaPagina: number) => {
        buscar(dados.filtro, novaPagina)
    }

    const handleResgatar = (cartela: CartelaItem) => {
        confirmar(
            "Confirmar resgate",
            `Confirmar o resgate da cartela de ${cartela.nome}?`,
            async () => {
                fecharDialogo()
                const autorizacao = localStorage.getItem("chaveSistema")
                if (!autorizacao) return
                try {
                    setLoading(true)
                    const res = await resgatarCartela(autorizacao, cartela.id)
                    if ("retornoErro" in res) {
                        enqueueSnackbar(res.mensagem || "Erro ao resgatar cartela.", {
                            variant: "error",
                        })
                    } else {
                        enqueueSnackbar(res.mensagem || "Cartela resgatada com sucesso!", {
                            variant: "success",
                        })
                        buscar(dados.filtro, dados.pagina)
                    }
                } finally {
                    setLoading(false)
                }
            }
        )
    }

    const handleReativar = (cartela: CartelaItem) => {
        confirmar(
            "Confirmar reativação",
            `Confirmar a reativação da cartela de ${cartela.nome}?`,
            async () => {
                fecharDialogo()
                const autorizacao = localStorage.getItem("chaveSistema")
                if (!autorizacao) return
                try {
                    setLoading(true)
                    const res = await reativarCartela(autorizacao, cartela.id)
                    if ("retornoErro" in res) {
                        enqueueSnackbar(res.mensagem || "Erro ao reativar cartela.", {
                            variant: "error",
                        })
                    } else {
                        enqueueSnackbar(res.mensagem || "Cartela reativada com sucesso!", {
                            variant: "success",
                        })
                        buscar(dados.filtro, dados.pagina)
                    }
                } finally {
                    setLoading(false)
                }
            }
        )
    }

    const handleExpirar = (status: number, label: string) => {
        confirmar(
            "Ação administrativa",
            `Tem certeza que deseja EXPIRAR TODAS as cartelas ${label} deste estabelecimento? O status será alterado para Expirada.`,
            async () => {
                fecharDialogo()
                const autorizacao = localStorage.getItem("chaveSistema")
                if (!autorizacao) return
                try {
                    setLoading(true)
                    const res = await expirarCartelas(autorizacao, status)
                    if ("retornoErro" in res) {
                        enqueueSnackbar(res.mensagem || "Erro ao expirar cartelas.", {
                            variant: "error",
                        })
                    } else {
                        enqueueSnackbar(`${res.quantidade} cartela(s) ${label} expirada(s).`, {
                            variant: "success",
                        })
                        buscar(dados.filtro, dados.pagina)
                    }
                } finally {
                    setLoading(false)
                }
            }
        )
    }

    const handleExportar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const res = await obterCartelas(autorizacao, dados.filtro, 0, 99999)
            if ("retornoErro" in res) {
                enqueueSnackbar(res.mensagem || "Erro ao exportar.", { variant: "error" })
            } else {
                gerarCsv(res.cartelas)
            }
        } finally {
            setLoading(false)
        }
    }

    const podeResgatar = (cartela: CartelaItem) =>
        cartela.status === "Completa" || dados.permiteResgateIncompleta

    const podeReativar = (cartela: CartelaItem) => cartela.status === "Expirada"

    return (
        <>
            <Paper className={classes.card}>
                <Box className={classes.cardHeader}>
                    <Typography className={classes.cardHeaderTitle}>
                        Cartelas ({dados.total} registro(s))
                    </Typography>
                    <Tooltip title="Exportar CSV com filtro atual">
                        <Button
                            variant="outlined"
                            size="small"
                            className={classes.btnExportar}
                            startIcon={<GetApp />}
                            onClick={handleExportar}
                        >
                            Exportar
                        </Button>
                    </Tooltip>
                </Box>

                <Box className={classes.cardContent}>
                    <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                        <Table
                            size="small"
                            stickyHeader
                            style={{ width: "100%", minWidth: 960 }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell style={{ minWidth: 48 }} />
                                    <TableCell style={{ minWidth: 160 }}>Nome</TableCell>
                                    <TableCell style={{ minWidth: 120 }}>Telefone</TableCell>
                                    <TableCell style={{ minWidth: 110 }}>Criação</TableCell>
                                    <TableCell style={{ minWidth: 110 }}>Validade</TableCell>
                                    <TableCell style={{ minWidth: 110 }}>Atualização</TableCell>
                                    <TableCell style={{ minWidth: 120 }}>Status</TableCell>
                                    <TableCell style={{ minWidth: 90 }} align="center">
                                        Selos
                                    </TableCell>
                                    <TableCell style={{ minWidth: 90 }} align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dados.cartelas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className={classes.semResultado}>
                                            A pesquisa não retornou resultados.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    dados.cartelas.map(cartela => (
                                        <TableRow key={cartela.id} hover>
                                            <TableCell>
                                                <Avatar
                                                    src={
                                                        cartela.facebookId
                                                            ? `https://graph.facebook.com/${cartela.facebookId}/picture?type=square`
                                                            : "https://s3-sa-east-1.amazonaws.com/clientefielsp/aplicacao/semfoto.png"
                                                    }
                                                    style={{ width: 32, height: 32 }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                className={classes.nomeCell}
                                                title={cartela.nome}
                                            >
                                                {cartela.nome}
                                            </TableCell>
                                            <TableCell>{cartela.telefone || "-"}</TableCell>
                                            <TableCell>
                                                {formatarData(cartela.dataCriacao)}
                                            </TableCell>
                                            <TableCell>
                                                {formatarData(cartela.dataValidade)}
                                            </TableCell>
                                            <TableCell>
                                                {formatarData(cartela.dataUltimaAtualizacao)}
                                            </TableCell>
                                            <TableCell>{chipStatus(cartela.status)}</TableCell>
                                            <TableCell align="center">
                                                {cartela.quantidadeSelos ?? "-"}
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                style={{ whiteSpace: "nowrap" }}
                                            >
                                                {podeResgatar(cartela) && (
                                                    <Tooltip title="Resgatar cartela">
                                                        <IconButton
                                                            size="small"
                                                            style={{ color: "#4caf50" }}
                                                            onClick={() => handleResgatar(cartela)}
                                                        >
                                                            <Done fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {podeReativar(cartela) && (
                                                    <Tooltip title="Reativar cartela">
                                                        <IconButton
                                                            size="small"
                                                            style={{ color: "#9e9e9e" }}
                                                            onClick={() => handleReativar(cartela)}
                                                        >
                                                            <Replay fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        count={dados.total}
                        page={dados.pagina}
                        rowsPerPage={dados.tamanhoPagina}
                        rowsPerPageOptions={[dados.tamanhoPagina]}
                        onPageChange={handleMudarPagina}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
                        }
                    />
                </Box>
            </Paper>

            {dados.isAdmin && (
                <Paper className={classes.adminCard}>
                    <Box className={classes.adminHeader}>
                        <Typography
                            style={{ fontWeight: 600, color: "var(--cor-marca-hover)", fontSize: "0.95rem" }}
                        >
                            Ações administrativas (Cliente Fiel)
                        </Typography>
                    </Box>
                    <Box className={classes.adminContent}>
                        <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                            Expira em massa as cartelas deste estabelecimento, alterando o status
                            para <strong>Expirada</strong>.
                        </Typography>
                        <Box display="flex" gridGap={16}>
                            <Button
                                variant="contained"
                                className={classes.btnExpirarAndamento}
                                startIcon={<Warning />}
                                onClick={() => handleExpirar(0, "em andamento")}
                            >
                                Expirar todas as cartelas em andamento
                            </Button>
                            <Button
                                variant="contained"
                                className={classes.btnExpirarCompletas}
                                startIcon={<Warning />}
                                onClick={() => handleExpirar(1, "completas")}
                            >
                                Expirar todas as cartelas completas
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            )}

            <Dialog open={dialogo.aberto} onClose={fecharDialogo}>
                <DialogTitle>{dialogo.titulo}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{dialogo.mensagem}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={fecharDialogo}>Não</Button>
                    <Button onClick={dialogo.onConfirmar} color="primary" autoFocus>
                        Sim
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
